import type { FirstPurchaseData } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import { dateFormatters, formatCurrencyNatural, match, P } from '@frontend/duck-tape';
import { getPlanSubtitle, usePaywallContext } from '@frontend/react-domain';
import { Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';

export type PurchaseDetailsCardProps = {
  firstPurchaseData: FirstPurchaseData;
};

export const PurchaseDetailsCard = ({ firstPurchaseData }: PurchaseDetailsCardProps) => {
  const { paywallState } = usePaywallContext();
  const { pricingTerminology } = usePricingTerminology();
  return (
    <YStack gapY="sm">
      {firstPurchaseData.selectedPurchaseType === 'Subscription' ? <Text type="h6">Your membership plan</Text> : null}
      <XStack
        alignItems="center"
        className="rounded-lg border-2 border-borderSecondary bg-trueWhite"
        gapX="md"
        justifyContent="between"
        p="md"
      >
        {match(firstPurchaseData)
          .with({ selectedPlanData: P.nonNullable, selectedPurchaseType: 'Subscription' }, ({ selectedPlanData }) => {
            const planPricing = getMembershipPlanDisplayPricing(selectedPlanData);

            return (
              <>
                <YStack>
                  <Text type="h5">{selectedPlanData.label}</Text>
                  <Text type="p-sm">{getPlanSubtitle(selectedPlanData, pricingTerminology)}</Text>
                </YStack>
                <YStack alignItems="end" gapY="xs">
                  <XStack alignItems="center">
                    <Text type="h3">{formatCurrencyNatural(planPricing?.primaryPrice ?? selectedPlanData.price)}</Text>
                    <Text className="italic" type="p-sm">
                      /mo
                    </Text>
                  </XStack>
                  {planPricing?.secondaryPrice ? (
                    <Text className="line-through" color="tertiary" type="p-sm">
                      {formatCurrencyNatural(planPricing.secondaryPrice)}/mo
                    </Text>
                  ) : null}
                </YStack>
              </>
            );
          })
          .with(
            { selectedCreditsPackage: P.nonNullable, selectedPurchaseType: 'ALaCarteCredits' },
            ({ selectedCreditsPackage }) => (
              <>
                <YStack>
                  <Text type="h5">{selectedCreditsPackage.amount} credits</Text>
                  <Text type="p-sm">{selectedCreditsPackage.description}</Text>
                </YStack>
                <Text type="h3">{formatCurrencyNatural(selectedCreditsPackage.priceCent)}</Text>
              </>
            ),
          )
          .otherwise(() => null)}
      </XStack>
      <Text className="italic text-textTertiary" type="caption">
        {firstPurchaseData.selectedPurchaseType === 'Subscription' && paywallState.promotionTranslation.nextBillingDate
          ? `Next billing date: ${dateFormatters['LL'](paywallState.promotionTranslation.nextBillingDate)}`
          : ''}
      </Text>
    </YStack>
  );
};
